import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { SITE_ORIGIN } from '@/lib/site-origin';

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Convert text to URL-safe slug
 */
export function toUrlSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Remove HTML tags from content strings
 */
export function stripHtmlTags(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Format price for display
 */
export function formatPrice(price: number, currency = 'INR'): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Format date for display
 */
export function formatDate(
  date: string | Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }
): string {
  return new Intl.DateTimeFormat('en-IN', options).format(new Date(date));
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

/**
 * Capitalize first letter of each word
 */
export function titleCase(text: string): string {
  return text
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Generate initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Check if running on client
 */
export function isClient(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Sleep for specified milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generate random ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

/**
 * Parse JSON safely
 */
export function safeJsonParse<T>(json: string, fallback: T): T {
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

/**
 * Build URL with query params
 */
export function buildUrl(
  base: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  if (!params) return base;

  const filteredParams = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

  return filteredParams ? `${base}?${filteredParams}` : base;
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Remove hyphens from slug and capitalize
 */
export function removeHyphen(text: string): string {
  return text
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Convert slug to title case
 */
export function toTitleCase(text: string): string {
  return text
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Normalize base URL (ensure no trailing slash)
 */
export function normalizeBaseUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

/**
 * Join URL paths
 */
export function joinUrl(...parts: string[]): string {
  return parts
    .map((part, index) => {
      if (index === 0) {
        return part.replace(/\/+$/, '');
      }
      return part.replace(/^\/+|\/+$/g, '');
    })
    .filter(Boolean)
    .join('/');
}

const DEFAULT_SITE_URL = SITE_ORIGIN;

function mergeRelValues(...rels: Array<string | undefined>): string | undefined {
  const merged = Array.from(
    new Set(
      rels
        .flatMap((rel) => rel?.split(/\s+/) || [])
        .map((value) => value.trim())
        .filter(Boolean)
    )
  );

  return merged.length > 0 ? merged.join(' ') : undefined;
}

/**
 * Normalise an SEO title so it always ends with " - DocuPro" exactly once.
 * Strips any existing " - DocuPro" / " | DocuPro" suffix the backend may have appended before re-adding.
 */
export function normalizeSeoTitle(rawTitle: string | null | undefined, fallback: string): string {
  const base = (rawTitle || fallback).replace(/\s*[-|]\s*DocuPro\s*$/i, '').trim();
  return `${base} - DocuPro`;
}

export function isExternalHttpUrl(href: string, baseUrl = DEFAULT_SITE_URL): boolean {
  if (!href) return false;

  try {
    const resolvedUrl = new URL(href, baseUrl);
    const normalizedBaseUrl = new URL(baseUrl);

    if (!['http:', 'https:'].includes(resolvedUrl.protocol)) {
      return false;
    }

    return resolvedUrl.origin !== normalizedBaseUrl.origin;
  } catch {
    return false;
  }
}

export function getExternalLinkRel(
  href: string,
  existingRel?: string,
  baseUrl = DEFAULT_SITE_URL
): string | undefined {
  if (!isExternalHttpUrl(href, baseUrl)) {
    return existingRel || undefined;
  }

  return mergeRelValues(existingRel, 'nofollow');
}

export function addNoFollowToExternalLinksInHtml(
  html: string,
  baseUrl = DEFAULT_SITE_URL
): string {
  if (!html) return html;

  return html.replace(/<a\b([^>]*?)\bhref=(['"])(.*?)\2([^>]*)>/gi, (match, beforeHref, quote, href, afterHref) => {
    if (!isExternalHttpUrl(href, baseUrl)) {
      return match;
    }

    const relMatch = match.match(/\brel=(['"])(.*?)\1/i);
    if (relMatch) {
      const mergedRel = getExternalLinkRel(href, relMatch[2], baseUrl);
      return mergedRel
        ? match.replace(relMatch[0], `rel=${relMatch[1]}${mergedRel}${relMatch[1]}`)
        : match;
    }

    return `<a${beforeHref}href=${quote}${href}${quote}${afterHref} rel="nofollow">`;
  });
}
